import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Check,
  Copy,
  Download,
  FileCode2,
  Image as ImageIcon,
  Printer,
} from "lucide-react";
import { loadBwip } from "@/lib/bwip";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FaqSection } from "@/components/seo/FaqSection";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { breadcrumbJsonLd, ldScript, pageHead, softwareAppJsonLd } from "@/lib/seo";
import { PrintSetupDialog } from "@/components/print/PrintSetupDialog";

const PATH = "/pdf417-generator";
const TITLE = "PDF417 Generator — Create 2D Stacked Barcodes Online";
const DESC =
  "Free PDF417 generator. Encode text, IDs or shipping data as a 2D PDF417 barcode with adjustable EC, columns and row height. PNG and SVG.";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Generators", path: "/generators" },
  { name: "PDF417 Generator", path: PATH },
];

const FAQ = [
  {
    q: "What Is PDF417?",
    a: "PDF417 is a 2D stacked linear barcode developed by Symbol Technologies in 1991. It encodes up to ~1850 characters of text (or ~1100 bytes of binary) in a rectangle of stacked rows, and is the symbology behind US/CA driver's licences (AAMVA), airline boarding passes (IATA BCBP) and many shipping labels.",
  },
  {
    q: "What Error Correction Level Should I Pick?",
    a: "PDF417 has 9 levels, 0–8. Each level doubles the number of error-correction codewords (2, 4, 8, … 512). Levels 2–3 are fine for clean, high-resolution prints. Levels 4–5 are the broad recommendation for general use. Levels 6+ are for harsh environments (laminated cards, dirty surfaces). Higher ECL means a bigger symbol for the same data.",
  },
  {
    q: "What Does 'Columns' Control?",
    a: "PDF417 lays codewords out in a grid of rows × columns. More columns = wider, fewer rows = shorter. Auto lets bwip-js pick a sensible ratio. Force a specific column count when you have to fit a fixed label width.",
  },
  {
    q: "When Should I Use PDF417 Compact?",
    a: "Compact PDF417 omits the right row indicators, saving 2 codewords of width per row. Use it when reader software supports it (most modern industrial scanners do) and you need to squeeze the symbol into a narrower label. It is not compatible with all legacy readers.",
  },
  {
    q: "What's the Largest Payload I Can Encode?",
    a: "Practical maximum is around 1850 alphanumeric characters or 2710 digits with EC level 0. Higher EC levels reduce that. If your data is too long, drop the EC level, raise the column count, or split into multiple symbols using PDF417 macro mode (not exposed in this tool).",
  },
  {
    q: "Is My Data Uploaded Anywhere?",
    a: "No. The barcode is rendered locally with the open-source bwip-js library. The text you encode and the files you download never leave your browser.",
  },
  {
    q: "How Do You Create a PDF417 Barcode?",
    a: "Type or paste your data into the box above, then pick an error-correction level and a column count; the symbol redraws as you type. Download it as PNG or SVG, or use Print to place it on a page. Scan the result once with the device that will read it before you use it in production.",
  },
  {
    q: "Can a Phone Scan a PDF417 Barcode?",
    a: "Yes, with an app that supports PDF417; many built-in camera apps only look for QR codes. The ReadBarcode barcode reader decodes PDF417 in the browser from the phone camera or from a photo. Hold the phone so the whole symbol, including the start and stop patterns at both ends, fits in the frame.",
  },
  {
    q: "What Is a Good PDF417 Alternative?",
    a: "Data Matrix holds similar data in a much smaller square and suits small parts. QR Code is the better choice when shoppers will scan with a phone camera, and Aztec needs no quiet zone, which helps on mobile tickets. Stay with PDF417 when a partner specification or the receiving scanner asks for it.",
  },
];

type ColumnsChoice = "auto" | string; // "auto" or "1".."30"
type EcLevel = "auto" | "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8";

// Practical caps. Real upper bound depends on chosen EC level and compaction
// mode; bwip-js will surface a clearer error if the encoder runs out of space.
const MAX_CHARS = 1850;

function validate(data: string): string | null {
  if (data.length === 0) return "Enter the data to encode.";
  if (data.length > MAX_CHARS)
    return `Data is ${data.length} chars — PDF417 maximum is ${MAX_CHARS} (alphanumeric mode).`;
  return null;
}

function safeFilename(data: string): string {
  const cleaned = data
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  return `pdf417-${cleaned || "code"}`;
}

function Pdf417GeneratorPage() {
  const [data, setData] = useState(
    "PDF417 is a 2D stacked barcode used on driver's licences, boarding passes and shipping labels.",
  );
  const [eclevel, setEclevel] = useState<EcLevel>("5");
  const [columns, setColumns] = useState<ColumnsChoice>("auto");
  const [rowHeight, setRowHeight] = useState(3);
  const [moduleScale, setModuleScale] = useState(2);
  const [compact, setCompact] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [svgMarkup, setSvgMarkup] = useState("");

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const validationErr = useMemo(() => validate(data), [data]);

  useEffect(() => {
    if (validationErr) {
      setError(validationErr);
      setSvgMarkup("");
      const c = canvasRef.current;
      if (c) c.getContext("2d")?.clearRect(0, 0, c.width, c.height);
      return;
    }
    let cancelled = false;
    loadBwip().then((bwipjs) => {
      if (cancelled) return;
      try {
        const opts: Record<string, unknown> & { bcid: string; text: string } = {
          bcid: compact ? "pdf417compact" : "pdf417",
          text: data,
          scale: moduleScale,
          // bwip-js's pdf417 encoder reads `rowmult` (row-height multiplier),
          // not `rowheight` — the latter is silently ignored, making the slider
          // a no-op.
          rowmult: rowHeight,
          backgroundcolor: "FFFFFF",
        };
        if (eclevel !== "auto") opts.eclevel = Number(eclevel);
        if (columns !== "auto") opts.columns = Number(columns);
        if (canvasRef.current) bwipjs.toCanvas(canvasRef.current, opts);
        setSvgMarkup(bwipjs.toSVG(opts));
        setError(null);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Failed to render PDF417.";
        setError(msg);
        setSvgMarkup("");
        const c = canvasRef.current;
        if (c) c.getContext("2d")?.clearRect(0, 0, c.width, c.height);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [data, eclevel, columns, rowHeight, moduleScale, compact, validationErr]);

  const downloadPng = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeFilename(data)}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  const downloadSvg = () => {
    if (!svgMarkup) return;
    const blob = new Blob([svgMarkup], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeFilename(data)}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyData = async () => {
    try {
      await navigator.clipboard.writeText(data);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="PDF417 Generator"
      description="Encode up to 1850 characters as a PDF417 2D stacked barcode. Adjustable error correction, columns and row height. Download as PNG or SVG — fully client-side. A Compact PDF417 switch narrows each row by dropping the right row indicator and shortening the stop pattern, and a module scale from 1x to 5x sets the image size."
      crumbs={CRUMBS}
      content={
        <>
          <ToolSeoSections
            aboutHeading="About the PDF417 generator"
            about={
              <>
                <p>
                  PDF417 is a stacked linear 2D symbology that packs up to ~1850 alphanumeric characters
                  (or about 1100 bytes of binary data) into a row-and-column grid. Unlike QR or Data
                  Matrix, PDF417 stays readable when individual rows are missing — it has been the
                  workhorse for US and Canadian driver&apos;s licences (AAMVA standard), airline
                  boarding passes (IATA BCBP), and a long tail of shipping, manifesting and government
                  ID applications.
                </p>
                <p>
                  This generator renders PDF417 directly in your browser with the open-source bwip-js
                  library. Tune the error-correction level, column count and row height for your label
                  size and printer; switch to PDF417 Compact when you need to squeeze the symbol into a
                  narrower space and your reader software supports it.
                </p>
              </>
            }
            whenToUse={[
              "Encoding driver's licence or government-issued ID data fields (AAMVA).",
              "Producing IATA BCBP boarding passes for pilots / agents.",
              "Shipping and manifesting labels where Data Matrix isn't acceptable.",
              "Storing structured records (CSV / JSON) up to ~1.8 KB on a single label.",
              "Marking high-value parts where partial damage shouldn't kill the read.",
              "Embedding cross-reference data on the back of a printed certificate or pass.",
            ]}
            howItWorks={[
              {
                title: "Paste your data",
                desc: "ASCII text, digits or Latin-1 — up to 1850 characters.",
              },
              {
                title: "Pick an EC level",
                desc: "Level 5 is the broad recommendation. Bump to 6+ for harsh surfaces, drop to 2–3 only for clean, high-DPI prints.",
              },
              {
                title: "Set columns / row height",
                desc: "Auto picks a balanced shape. Force a column count to hit a fixed label width.",
              },
              {
                title: "Tune module scale",
                desc: "Bigger = scans further at the cost of label real estate.",
              },
              {
                title: "Download PNG or SVG",
                desc: "SVG for print, PNG for screen mock-ups and slide decks.",
              },
            ]}
            whatToAvoid={[
              "Don't drop EC below 3 for plastic / laminated cards — even small smudges can break the symbol.",
              "Don't force a tiny column count for a long payload — the row count balloons and scanning gets unreliable.",
              "Don't use Compact PDF417 unless you control the scanner — many legacy readers fail on it.",
              "Don't print smaller than ~4 mil module width on thermal printers — bar-edge growth eats the row indicators.",
              "Don't shove binary data through without telling downstream code what encoding to expect.",
            ]}
            tips={[
              "Export as SVG and rasterise to PNG only for the final asset — vector means you can scale to any printer.",
              "Round-trip-test by scanning the generated label with the device that will read it in production.",
              "For boarding passes follow the IATA BCBP field layout exactly; readers reject anything off-spec.",
              "If you can choose, AAMVA-style ID payloads should use EC level 5 or 6 to survive laminated card wear.",
              "Compact PDF417 only saves width — if you're vertically constrained, raise the column count instead.",
            ]}
          />
          <PrivacyNote />
          <Pdf417Sections />
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-5 p-6">
            <div className="space-y-2">
              <Label htmlFor="data">Data to encode</Label>
              <Textarea
                id="data"
                value={data}
                onChange={(e) => setData(e.target.value)}
                rows={5}
                spellCheck={false}
                autoComplete="off"
                className="font-mono text-sm"
                placeholder="Paste up to 1850 characters…"
              />
              <p className="text-xs text-muted-foreground">
                {data.length} / {MAX_CHARS} characters. PDF417 supports ASCII text, digits and
                Latin-1 binary.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="eclevel">Error correction</Label>
                <Select value={eclevel} onValueChange={(v) => setEclevel(v as EcLevel)}>
                  <SelectTrigger id="eclevel">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto</SelectItem>
                    <SelectItem value="0">Level 0 — 2 codewords</SelectItem>
                    <SelectItem value="1">Level 1 — 4 codewords</SelectItem>
                    <SelectItem value="2">Level 2 — 8 codewords</SelectItem>
                    <SelectItem value="3">Level 3 — 16 codewords</SelectItem>
                    <SelectItem value="4">Level 4 — 32 codewords</SelectItem>
                    <SelectItem value="5">Level 5 — 64 codewords (recommended)</SelectItem>
                    <SelectItem value="6">Level 6 — 128 codewords</SelectItem>
                    <SelectItem value="7">Level 7 — 256 codewords</SelectItem>
                    <SelectItem value="8">Level 8 — 512 codewords</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="columns">Columns</Label>
                <Select value={columns} onValueChange={(v) => setColumns(v as ColumnsChoice)}>
                  <SelectTrigger id="columns">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="auto">Auto</SelectItem>
                    {Array.from({ length: 30 }, (_, i) => i + 1).map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n} column{n === 1 ? "" : "s"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Row height</Label>
                <span className="text-xs text-muted-foreground">{rowHeight} modules</span>
              </div>
              <Slider
                min={2}
                max={5}
                step={1}
                value={[rowHeight]}
                onValueChange={(v) => setRowHeight(v[0])}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Module scale</Label>
                <span className="text-xs text-muted-foreground">{moduleScale}x</span>
              </div>
              <Slider
                min={1}
                max={5}
                step={1}
                value={[moduleScale]}
                onValueChange={(v) => setModuleScale(v[0])}
              />
            </div>

            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <Label htmlFor="compact" className="cursor-pointer">
                  Compact PDF417
                </Label>
                <p className="text-xs text-muted-foreground">
                  Drops the right row indicators — saves width but needs a modern scanner.
                </p>
              </div>
              <Switch id="compact" checked={compact} onCheckedChange={setCompact} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex h-full flex-col gap-4 p-6">
            <div className="flex min-h-[280px] flex-1 items-center justify-center rounded-md border border-dashed border-border bg-white p-4">
              {error ? (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" aria-hidden />
                  <span>{error}</span>
                </div>
              ) : (
                <canvas
                  ref={canvasRef}
                  className="max-h-[420px] max-w-full"
                  aria-label="Generated PDF417 barcode preview"
                />
              )}
            </div>

            <div className="rounded-md border border-border bg-muted/30 p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-medium text-muted-foreground">Encoded payload</div>
                  <code className="mt-1 block max-h-24 overflow-auto break-all text-xs">
                    {data || "—"}
                  </code>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={copyData} disabled={!data}>
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Button onClick={downloadPng} disabled={!!error}>
                <ImageIcon className="h-4 w-4" aria-hidden />
                PNG
              </Button>
              <Button variant="outline" onClick={downloadSvg} disabled={!!error || !svgMarkup}>
                <FileCode2 className="h-4 w-4" aria-hidden />
                SVG
              </Button>
              <Button
                variant="outline"
                onClick={() => setPrintOpen(true)}
                disabled={!!error || !svgMarkup}
              >
                <Printer className="h-4 w-4" aria-hidden />
                Print
              </Button>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Download className="h-3.5 w-3.5" aria-hidden />
              Files are generated on your device — nothing is uploaded.
            </p>
          </CardContent>
        </Card>
      </div>

      <PrintSetupDialog
        open={printOpen}
        onOpenChange={setPrintOpen}
        title={`PDF417 · ${data.slice(0, 60) || "code"}`}
        filename={safeFilename(data)}
        svgMarkup={svgMarkup}
        defaultCaption={data}
        defaults={{
          layoutKind: "single",
          widthMm: 90,
          paper: "A4",
          orientation: "portrait",
        }}
      />
    </ToolLayout>
  );
}

function PrivacyNote() {
  return (
    <>
      <h2>Private by Default</h2>
      <p>
        PDF417 barcodes are rendered locally with bwip-js. The text you encode never touches our
        servers.
      </p>
    </>
  );
}

function Pdf417Sections() {
  return (
    <>
      <h2>What Is PDF417?</h2>
      <p>PDF417 is a stacked 2D barcode: 3 to 90 rows of codewords, each row framed by indicators that tell the scanner which row it is reading.</p>
      <p>The name joins Portable Data File with the codeword shape of 4 bars and 4 spaces across 17 modules. Each row holds 1 to 30 data columns.</p>
      <h3>PDF417 Error Correction Levels</h3>
      <p>Levels 0 to 8 add from 2 to 512 Reed-Solomon codewords. ISO/IEC 15438 recommends at least level 2 for up to 40 data codewords, level 3 up to 160, level 4 up to 320 and level 5 above that.</p>
      <h2>PDF417 Barcode Generator for Shipping Labels and Tickets</h2>
      <p>This PDF417 barcode generator fits labels and tickets where one symbol carries a whole record, such as a shipment reference or an event booking.</p>
      <ol>
        <li>Fix the column count to match the label width.</li>
        <li>Keep a quiet zone of at least 2 modules on every side.</li>
        <li>Print a test and read it with the <Link to="/barcode-reader">online barcode reader</Link>.</li>
      </ol>
      <p>The <Link to="/barcode-guides/barcode-formats">barcode formats guide</Link> compares PDF417 with Aztec, QR and Data Matrix.</p>
    </>
  );
}

function RelatedTools() {
  const related = [
    {
      path: "/barcode-reader",
      label: "Barcode Reader",
      desc: "Scan a PDF417 barcode from your camera or an image.",
    },
    {
      path: "/barcode-guides/barcode-formats",
      label: "Barcode Formats Guide",
      desc: "How PDF417 compares with the other 1D and 2D formats.",
    },
    {
      path: "/data-matrix-generator",
      label: "Data Matrix Generator",
      desc: "Compact 2D barcode for small parts and pharma.",
    },
    {
      path: "/qr-code-generator",
      label: "QR Code Generator",
      desc: "Consumer-friendly 2D barcode for phone scanners.",
    },
    {
      path: "/barcode-generator",
      label: "Online Barcode Generator",
      desc: "Classic 1D symbologies — UPC, EAN, Code 128 and more.",
    },
    {
      path: "/barcode-guides/1d-vs-2d-barcodes",
      label: "1D vs 2D barcodes",
      desc: "When to reach for PDF417 vs QR vs Data Matrix.",
    },
    {
      path: "/barcode-type-detector",
      label: "Barcode Type Detector",
      desc: "Identify a barcode value's likely format.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-lg font-semibold">Related Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {related.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="group rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
          >
            <div className="text-sm font-medium group-hover:text-primary">{r.label}</div>
            <div className="text-xs text-muted-foreground">{r.desc}</div>
          </a>
        ))}
      </div>
    </section>
  );
}

export const Route = createFileRoute("/pdf417-generator")({
  component: Pdf417GeneratorPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
    });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode PDF417 Generator",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});
